import { useCallback } from "react";
import { X } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import {
  CollabDraftAnalyticsColumn,
  CollabDraftTestingTable,
  CollabProposedAttribute,
  CollabRemovedItem,
  CollabTestingTableChanges,
} from "../../../models/audit/collabPlanTypes";
import { RequestDelete } from "./planReveal";
import { withRemovedInPlace } from "./planGhosts";
import { collabFieldKeys as fieldKeys } from "./planFieldKeys";
import {
  CollapsibleRow,
  EditableDetailField,
  FieldRevealAnchor,
  GhostDetailField,
  RemovedBadge,
} from "./planFields";
import { formatDatasetSources } from "../../../models/audit/analyticsDraftedTest";
import {
  reviewAnalyticsColumnsAuthoringOffCopy,
  useHybridTestingAuthoring,
} from "../../../hooks/useHybridTestingAvailability";
import type { AnalyticsOwnerKind } from "../../../lib/analyticsOwner";

/**
 * One testing table's analytics columns in plan review: the kept ones, editable
 * while authoring is on, ghosts of the ones the latest revision dropped, and
 * the saved ones the plan removes on save, each with Undo where the server
 * would take it back.
 */
export const CollabAnalyticsColumnsSection = <
  TTable extends CollabDraftTestingTable,
>({
  table,
  removedGhosts,
  editable,
  proposedAttributes,
  onTableUpdate,
  requestDelete,
  analyticsOwnerKind,
}: {
  table: TTable;
  removedGhosts: CollabRemovedItem<CollabDraftAnalyticsColumn>[];
  editable: boolean;
  proposedAttributes: CollabProposedAttribute[];
  onTableUpdate?: (table: TTable, changes: CollabTestingTableChanges) => void;
  requestDelete: RequestDelete;
  analyticsOwnerKind: AnalyticsOwnerKind;
}) => {
  // Off the authoring flags an analytics column's definition is read-only
  // here, for a saved column and for a proposal left from before the flags
  // turned off alike. Removing either stays allowed.
  const { availability, authoringOff: authoringOffNoteShown } =
    useHybridTestingAuthoring(analyticsOwnerKind);
  const analyticsFieldsEditable = editable && availability !== "hidden";

  const updateAnalyticsColumn = useCallback(
    (
      table: TTable,
      colIndex: number,
      changes: Partial<CollabDraftAnalyticsColumn>,
    ) => {
      const analyticsColumns = table.analyticsColumns.map((col, i) =>
        i === colIndex ? { ...col, ...changes } : col,
      );
      onTableUpdate?.(table, { analyticsColumns });
    },
    [onTableUpdate],
  );

  const discardAnalyticsColumn = useCallback(
    (table: TTable, colIndex: number) => {
      const analyticsColumns = table.analyticsColumns.filter(
        (_, i) => i !== colIndex,
      );
      onTableUpdate?.(table, { analyticsColumns });
    },
    [onTableUpdate],
  );

  // A saved column is deleted on save only when the plan records its removal;
  // dropping it from analytics_columns alone is refused by the server.
  const removeSavedAnalyticsColumn = useCallback(
    (table: TTable, colIndex: number) => {
      const removed = table.analyticsColumns[colIndex];
      if (!removed) {
        return;
      }
      onTableUpdate?.(table, {
        analyticsColumns: table.analyticsColumns.filter(
          (_, i) => i !== colIndex,
        ),
        removedAnalyticsColumns: [...table.removedAnalyticsColumns, removed],
      });
    },
    [onTableUpdate],
  );

  const undoAnalyticsColumnRemoval = useCallback(
    (table: TTable, removedIndex: number) => {
      const restored = table.removedAnalyticsColumns[removedIndex];
      if (!restored) {
        return;
      }
      onTableUpdate?.(table, {
        analyticsColumns: [...table.analyticsColumns, restored],
        removedAnalyticsColumns: table.removedAnalyticsColumns.filter(
          (_, i) => i !== removedIndex,
        ),
      });
    },
    [onTableUpdate],
  );

  if (
    table.analyticsColumns.length === 0 &&
    removedGhosts.length === 0 &&
    table.removedAnalyticsColumns.length === 0
  ) {
    return null;
  }

  const columnEntries = withRemovedInPlace(
    table.analyticsColumns,
    removedGhosts,
  );

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-stone-600">
        Analytics Columns ({table.analyticsColumns.length})
      </div>
      {editable && authoringOffNoteShown && (
        <p className="text-xs text-stone-500">
          {reviewAnalyticsColumnsAuthoringOffCopy(analyticsOwnerKind)}
        </p>
      )}
      <div className="space-y-1.5">
        {columnEntries.map((entry) => {
          if (entry.kind === "removed") {
            const col = entry.item;
            return (
              <FieldRevealAnchor
                key={`ghost-${col.id ?? col.name}`}
                fieldKey={fieldKeys.analyticsName(table.name, col.name)}
              >
                <CollapsibleRow
                  className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2"
                  header={
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-red-400 line-through">
                        {col.name}
                      </span>
                      {col.attributeReference && (
                        <span className="rounded-sm bg-red-100 px-1.5 py-0.5 text-[11px] text-red-600 line-through">
                          {col.attributeReference}
                        </span>
                      )}
                      <RemovedBadge />
                    </span>
                  }
                >
                  <div className="space-y-1.5">
                    <GhostDetailField label="Pass" value={col.passCriteria} />
                    <GhostDetailField label="Fail" value={col.failCriteria} />
                    <GhostDetailField label="N/A" value={col.naCriteria} />
                    <GhostDetailField
                      label="Procedure Guidance"
                      value={col.procedureGuidance}
                    />
                    <GhostDetailField
                      label="Tolerances"
                      value={col.tolerances}
                    />
                    <GhostDetailField
                      label="Expected Evidence"
                      value={col.expectedEvidences}
                    />
                    <GhostDetailField
                      label="Drafted Test"
                      value={col.draftedTest}
                    />
                  </div>
                </CollapsibleRow>
              </FieldRevealAnchor>
            );
          }
          const col = entry.item;
          const i = entry.index;
          const datasetLine = formatDatasetSources(col.draftedTestDatasets);
          return (
            <FieldRevealAnchor
              key={col.id ?? `proposed-${i}`}
              fieldKey={fieldKeys.analyticsName(table.name, col.name)}
            >
              <CollapsibleRow
                revealPrefixes={[`analytics:${table.name}:${col.name}:`]}
                className="group/col rounded-md border border-stone-200 bg-white px-2.5 py-2"
                header={
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-stone-700">
                      {col.name}
                    </span>
                    {col.attributeReference && (
                      <span className="rounded-sm bg-stone-200 px-1.5 py-0.5 text-[11px] text-stone-600">
                        {col.attributeReference}
                      </span>
                    )}
                    <Badge
                      variant="outline"
                      className="rounded-sm px-1.5 py-0.5 text-[11px] font-normal"
                    >
                      Analytics
                    </Badge>
                    {/* Only a proposal is known to need setup: it
                      links no workflow. A saved column's setup is
                      not carried by the plan. */}
                    {!col.id && (
                      <Badge
                        variant="secondary"
                        className="rounded-sm px-1.5 py-0.5 text-[11px] font-normal"
                      >
                        Needs setup
                      </Badge>
                    )}
                  </span>
                }
                action={
                  editable ? (
                    col.id ? (
                      <button
                        type="button"
                        onClick={() =>
                          requestDelete({
                            title: "Remove analytics column?",
                            description: `"${col.name}" will be deleted from ${table.name} when the plan is saved. Any Optro workflow linked to it is not deleted.`,
                            onConfirm: () =>
                              removeSavedAnalyticsColumn(table, i),
                          })
                        }
                        className="rounded-sm p-1 text-stone-400 opacity-0 transition-opacity hover:bg-stone-200 hover:text-stone-600 group-hover/col:opacity-100"
                        aria-label="Remove column"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          requestDelete({
                            title: "Discard analytics column?",
                            description: `"${col.name}" will not be added to ${table.name}.`,
                            onConfirm: () => discardAnalyticsColumn(table, i),
                          })
                        }
                        className="rounded-sm p-1 text-stone-400 opacity-0 transition-opacity hover:bg-stone-200 hover:text-stone-600 group-hover/col:opacity-100"
                        aria-label="Discard column"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )
                  ) : undefined
                }
              >
                <div className="space-y-1.5">
                  <EditableDetailField
                    label="Name"
                    value={col.name}
                    editable={analyticsFieldsEditable}
                    placeholder="Column name..."
                    onCommit={(v) =>
                      updateAnalyticsColumn(table, i, {
                        name: v || col.name,
                      })
                    }
                    fieldKey={fieldKeys.analyticsName(table.name, col.name)}
                    // The row's anchor carries this same key and flashes.
                    suppressReveal
                    singleLine
                  />
                  <EditableDetailField
                    label="Pass"
                    value={col.passCriteria}
                    editable={analyticsFieldsEditable}
                    placeholder="Pass criteria..."
                    onCommit={(v) =>
                      updateAnalyticsColumn(table, i, {
                        passCriteria: v || col.passCriteria,
                      })
                    }
                    fieldKey={fieldKeys.analyticsPass(table.name, col.name)}
                  />
                  <EditableDetailField
                    label="Fail"
                    value={col.failCriteria}
                    editable={analyticsFieldsEditable}
                    placeholder="Fail criteria..."
                    onCommit={(v) =>
                      updateAnalyticsColumn(table, i, {
                        failCriteria: v || null,
                      })
                    }
                    fieldKey={fieldKeys.analyticsFail(table.name, col.name)}
                  />
                  <EditableDetailField
                    label="N/A"
                    value={col.naCriteria}
                    editable={analyticsFieldsEditable}
                    placeholder="N/A criteria..."
                    onCommit={(v) =>
                      updateAnalyticsColumn(table, i, {
                        naCriteria: v || null,
                      })
                    }
                    fieldKey={fieldKeys.analyticsNa(table.name, col.name)}
                  />
                  <EditableDetailField
                    label="Procedure Guidance"
                    value={col.procedureGuidance}
                    editable={analyticsFieldsEditable}
                    placeholder="Procedure guidance..."
                    onCommit={(v) =>
                      updateAnalyticsColumn(table, i, {
                        procedureGuidance: v || null,
                      })
                    }
                    fieldKey={fieldKeys.analyticsGuidance(table.name, col.name)}
                  />
                  <EditableDetailField
                    label="Tolerances"
                    value={col.tolerances}
                    editable={analyticsFieldsEditable}
                    placeholder="Tolerances..."
                    onCommit={(v) =>
                      updateAnalyticsColumn(table, i, {
                        tolerances: v || null,
                      })
                    }
                    fieldKey={fieldKeys.analyticsTolerances(
                      table.name,
                      col.name,
                    )}
                  />
                  <EditableDetailField
                    label="Expected Evidence"
                    value={col.expectedEvidences}
                    editable={analyticsFieldsEditable}
                    placeholder="Expected evidence..."
                    onCommit={(v) =>
                      updateAnalyticsColumn(table, i, {
                        expectedEvidences: v || null,
                      })
                    }
                    fieldKey={fieldKeys.analyticsExpectedEvidences(
                      table.name,
                      col.name,
                    )}
                  />
                  <EditableDetailField
                    label="Drafted Test"
                    value={col.draftedTest}
                    editable={analyticsFieldsEditable}
                    placeholder="The test to build in Optro Analytics..."
                    onCommit={(v) =>
                      updateAnalyticsColumn(table, i, {
                        draftedTest: v || null,
                      })
                    }
                    fieldKey={fieldKeys.analyticsDraftedTest(
                      table.name,
                      col.name,
                    )}
                  />
                  {datasetLine && (
                    <p className="text-xs text-stone-600">{datasetLine}</p>
                  )}
                  {/* A suggestion only: the category is chosen in
                    the column dialog when the workflow is built. */}
                  {col.suggestedCategoryName && (
                    <p className="text-xs text-stone-600">
                      Suggested category:{" "}
                      <span className="font-medium text-stone-700">
                        {col.suggestedCategoryName}
                      </span>
                    </p>
                  )}
                </div>
              </CollapsibleRow>
            </FieldRevealAnchor>
          );
        })}
        {table.removedAnalyticsColumns.map((col, i) => {
          // The server refuses a restored column whose attribute
          // the plan no longer has, so Undo is not offered; one
          // it already carried unmapped stays restorable.
          const restorable =
            col.attributeReference == null ||
            proposedAttributes.some(
              (attribute) => attribute.reference === col.attributeReference,
            );
          return (
            <FieldRevealAnchor
              key={`removed-${col.id ?? i}`}
              fieldKey={fieldKeys.analyticsName(table.name, col.name)}
            >
              <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2">
                <CollapsibleRow
                  header={
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-red-400 line-through">
                        {col.name}
                      </span>
                      {col.attributeReference && (
                        <span className="rounded-sm bg-red-100 px-1.5 py-0.5 text-[11px] text-red-600 line-through">
                          {col.attributeReference}
                        </span>
                      )}
                      <RemovedBadge />
                    </span>
                  }
                  action={
                    editable && restorable ? (
                      <button
                        type="button"
                        onClick={() => undoAnalyticsColumnRemoval(table, i)}
                        className="shrink-0 rounded-sm px-1.5 py-0.5 text-xs font-medium text-stone-600 hover:bg-red-100 hover:text-stone-800"
                      >
                        Undo
                      </button>
                    ) : undefined
                  }
                >
                  <div className="space-y-1.5">
                    <GhostDetailField label="Pass" value={col.passCriteria} />
                    <GhostDetailField label="Fail" value={col.failCriteria} />
                    <GhostDetailField label="N/A" value={col.naCriteria} />
                    <GhostDetailField
                      label="Procedure Guidance"
                      value={col.procedureGuidance}
                    />
                    <GhostDetailField
                      label="Tolerances"
                      value={col.tolerances}
                    />
                    <GhostDetailField
                      label="Expected Evidence"
                      value={col.expectedEvidences}
                    />
                    <GhostDetailField
                      label="Drafted Test"
                      value={col.draftedTest}
                    />
                  </div>
                </CollapsibleRow>
                {/* Outside the toggle, so the notes stay visible while the
                  row is collapsed without becoming its accessible name. */}
                <div className="mt-0.5 space-y-0.5 pl-6">
                  <p className="text-xs text-red-700">
                    Removes {col.name} on save. Any Optro workflow linked to it
                    is not deleted.
                  </p>
                  {editable && !restorable && (
                    <p className="text-xs text-stone-500">
                      Can&apos;t undo: its attribute was removed from the plan.
                    </p>
                  )}
                </div>
              </div>
            </FieldRevealAnchor>
          );
        })}
      </div>
    </div>
  );
};
